/**
 * hauler_tenant_settings — per-tenant hauler configuration.
 *
 * Phase 6 / Plan 06-01 / D-12 / D-22 step 9.
 *
 * PK is `tenant_id` (one row per tenant, lazily inserted by routes that need
 * the settings — Plan 06-06 stale-paused query + Plan 06-08 color-cut UI).
 * RLS-scoped via the standard `tenant_id = current_setting('app.current_tenant_id')::uuid`
 * predicate so the row a tenant sees is always their own.
 *
 * Columns:
 *   - stale_paused_warning_hours (integer, default 2) — D-12 threshold for the
 *     amber "stale paused job" flag in the dispatcher view (Phase 9).
 *   - color_cut_options (jsonb, nullable) — OPEN-001 tenant-overridable list
 *     of color-cut chip values. NULL = mobile uses the placeholder list
 *     `['None','Trace','1 in','2 in','3 in','4 in','Custom']`; non-NULL =
 *     tenant has explicitly configured their list (Phase 9 or 6.5 admin UI).
 *
 * Why a new table (not extending tenants):
 *   - `tenants` is a platform-level table holding cross-tenant catalog data
 *     (id, slug, name, tenant_type, driver_job_reject_enabled). Layering
 *     hauler-specific knobs onto it would force operator/disposal/platform
 *     tenants to carry irrelevant columns.
 *   - This table is hauler-tenant-specific. Future hauler-only flags (color
 *     cut overrides, billing-template defaults, etc.) land here too.
 *
 * RLS posture: pgPolicy `hauler_tenant_settings_isolation` mirrors the
 * verbatim `operators_isolation` analog from `sites.ts:129`. The SQL
 * migration 0005 step 9 creates the same-named DB-side policy. Mirroring is
 * MANDATORY per MEDIUM-4 / Plan 06-01 — schema-diff tooling stays aligned
 * with the DB-side policies.
 *
 * Why this differs from `user-device-pins.ts`:
 *   - user-device-pins is NOT tenant-scoped (PINs follow the user, not the
 *     tenant) and has NO pgPolicy block.
 *   - hauler_tenant_settings IS tenant-scoped and MUST have a pgPolicy block.
 *
 * Sources:
 *   - master spec §13 (color_cut_options tenant setting)
 *   - Phase 6 / Plan 06-01 / D-12 (stale_paused_warning_hours default 2h)
 *   - Phase 6 / Plan 06-01 / D-22 step 9 (table shape)
 *   - Plan 06-01 (pgPolicy mirroring per MEDIUM-4)
 *   - OPEN-001 (color_cut placeholder list, tenant-overridable)
 */

import {
  boolean,
  integer,
  jsonb,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';

/**
 * DEC-329 — whether dispatch may force a stuck ticket to review (DEC-321):
 *   NEVER               — no one may;
 *   SUPERVISOR_REQUIRED — a plain dispatcher's force becomes a request a manager approves;
 *   ALWAYS              — dispatch forces directly, with a reason.
 */
export const DISPATCHER_COMPLETION_POLICIES = ['NEVER', 'SUPERVISOR_REQUIRED', 'ALWAYS'] as const;
export type DispatcherCompletionPolicy = (typeof DISPATCHER_COMPLETION_POLICIES)[number];
/** What a company with no settings row gets: the column default (new companies). */
export const DEFAULT_DISPATCHER_COMPLETION_POLICY: DispatcherCompletionPolicy =
  'SUPERVISOR_REQUIRED';

/**
 * Migration 0250 (hauler billing gap 6, Corbin 2026-10-21) — which day a ticket is billed on,
 * its service date:
 *   FINISHED — the day the job finished (completed_at). The default, and what billing did
 *              before 0250, so nothing changes for a hauler until they switch.
 *   STARTED  — the day the work began: the customer's billing-start marker, as the hourly
 *              clock uses it (ARRIVAL = arrived at pickup, START = start of work).
 * The day is the company's local day (`timezone`). services/billing/service-date.ts.
 */
export const BILL_DATE_BASES = ['FINISHED', 'STARTED'] as const;
export type BillDateBasis = (typeof BILL_DATE_BASES)[number];
export const DEFAULT_BILL_DATE_BASIS: BillDateBasis = 'FINISHED';

export const haulerTenantSettings = pgTable(
  'hauler_tenant_settings',
  {
    tenantId: uuid('tenant_id')
      .primaryKey()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // D-12 — stale-paused threshold. Default 2 hours; tenant-overridable.
    stalePausedWarningHours: integer('stale_paused_warning_hours').notNull().default(2),
    // DEC-329 (migration 0154) — may dispatch force a stuck ticket to review? The column
    // default is for NEW companies; 0154 set every existing company to ALWAYS (Corbin D-D).
    dispatcherCompletionPolicy: text('dispatcher_completion_policy')
      .$type<DispatcherCompletionPolicy>()
      .notNull()
      .default('SUPERVISOR_REQUIRED'),
    // OPEN-001 — color-cut chip overrides. NULL = mobile uses the placeholder list.
    colorCutOptions: jsonb('color_cut_options'),
    // live_map_enabled (0026) is gone: the live map is the LIVE_MAP feature (§58, DEC-366).
    // Migration 0173 (DEC-351) — show ESTIMATED tank levels, fill rates and full dates
    // (derived from SINGLE_GAUGE top gauge × bbl_per_inch). Default true. When false, the
    // operator portal and dispatcher Tanks view show recorded levels only. Applied per
    // tank by the tank's OWNING hauler, so an operator served by two haulers sees each
    // hauler's choice on that hauler's tanks.
    tankEstimatesEnabled: boolean('tank_estimates_enabled').notNull().default(true),
    // tanks_tab_enabled (0192) is gone: the Tanks tab is the TANKS_TAB feature (§58 PR 6a,
    // DEC-366); a company that had it off carries a TANKS_TAB override (0197).

    // Migration 0084 — IANA timezone for this tenant's LOCAL day.
    // 0080 shipped dayOfWeek/dayOfMonth with nothing to evaluate them against:
    // "the 1st of the month" is a moment per timezone, and the server's UTC day
    // rolls over hours before a Texas hauler's does. DEC-004/DEC-066 already
    // require date-bounded logic to bucket by local day; this is that rule
    // applied to scheduling.
    timezone: text('timezone').notNull().default('America/Chicago'),

    // ── QuickBooks Desktop / Transaction Pro Importer export (migration 0080) ──
    // ClickUp 86baxrd1j. Only QBD is implemented. 'QBO' is a legal value so a
    // future migration need not rewrite the CHECK, but the export service
    // rejects it and no UI offers it — Intuit OAuth / app registration is
    // deferred past November.
    accountingExportTarget: text('accounting_export_target').notNull().default('QBD'),
    // ITEMS = each line posts as a QuickBooks Item; FLAT_ACCOUNT = a dollar
    // amount to one revenue account. FLAT_ACCOUNT is the default because an
    // Items export with no item names is malformed, whereas a flat export is
    // always well-formed — the safer state for an unconfigured tenant.
    accountingLineMode: text('accounting_line_mode').notNull().default('FLAT_ACCOUNT'),
    accountingExportCadence: text('accounting_export_cadence').notNull().default('ON_DEMAND'),
    // Shape: { frequency: 'WEEKLY'|'MONTHLY', dayOfWeek?: 0-6, dayOfMonth?: 1-28,
    //          recipients: string[] }. NOT NULL is enforced by a CHECK whenever
    // cadence is SCHEDULED, so "scheduled with no schedule" is unrepresentable.
    accountingScheduleConfig: jsonb('accounting_schedule_config'),
    // Migration 0084 — the watermark. Set when cadence moves ON_DEMAND ->
    // SCHEDULED, cleared on the way back. The sweeper clamps every period to
    // start no earlier than this local date, so enabling a schedule does NOT
    // mail out invoices that were already exported on demand and imported into
    // QuickBooks. A CHECK makes SCHEDULED-without-a-watermark unrepresentable.
    accountingScheduleEnabledAt: timestamp('accounting_schedule_enabled_at', {
      withTimezone: true,
    }),
    // Migration 0137 (DEC-326) — where export-run history starts. Rows that
    // existed at the migration got its time; later rows are NULL ("tracked from
    // the beginning"). Invoices sent before it are left out of "not yet
    // exported", because exports before it were never recorded.
    accountingExportTrackingSince: timestamp('accounting_export_tracking_since', {
      withTimezone: true,
    }),
    // Migration 0145 (DEC-326 item 3a) — the company fuel cost per mile, integer cents.
    // NULL = not set: loads show miles but no fuel cost. CHECK 0..100000 in the migration.
    fuelCostCentsPerMile: integer('fuel_cost_cents_per_mile'),
    // Migration 0150 (DEC-326 item 5) — the company's on-time rule. ALLOWANCE X: late =
    // more than X% over the route's median (0–500; default 25). PERCENTILE X: late =
    // slower than the route's p(X x 10) (1–10). CHECK in the migration.
    onTimeMethod: text('on_time_method').notNull().default('ALLOWANCE'),
    onTimeValue: integer('on_time_value').notNull().default(25),
    // Migration 0159 (OPEN-110) — a shift with no driver activity for this many hours is
    // closed at its last activity (hourly sweep, and at the next shift start). Whole hours
    // 4–16, default 8; CHECK in the migration.
    shiftIdleCloseHours: integer('shift_idle_close_hours').notNull().default(8),

    // ── Disposal recommendations (migration 0151, Phase 28, DEC-331) ──
    // §60 master switch, default OFF. While off, nobody sees a suggestion, but the app
    // still works one out and records it silently. Cannot be turned on while fuel or
    // driver cost is NULL (enforced by the settings route, D-4).
    disposalRecommendationEnabled: boolean('disposal_recommendation_enabled')
      .notNull()
      .default(false),
    // 'FULL_COST' | 'PRIORITY_ONLY' (CHECK in the migration).
    disposalRecDispatcherDisplay: text('disposal_rec_dispatcher_display')
      .notNull()
      .default('FULL_COST'),
    // 'FULL_COST' | 'PRIORITY_ONLY' | 'HIDDEN' (CHECK in the migration).
    disposalRecDriverDisplay: text('disposal_rec_driver_display').notNull().default('FULL_COST'),
    // Integer cents per hour; NULL = not set. Sits beside fuel cost on the Setup page.
    driverCostCentsPerHour: integer('driver_cost_cents_per_hour'),
    avgSpeedMph: integer('avg_speed_mph').notNull().default(45),
    // The dispatcher's suggestion before a load exists, when the pickup has no history (D-11).
    typicalLoadBbl: integer('typical_load_bbl').notNull().default(100),
    // NOTE: there is deliberately NO class-dimension column and no mapping
    // table. QuickBooks Class tracking was deferred 2026-09-01. The CSV still
    // emits an always-blank Class column so the office's saved Transaction Pro
    // mapping already covers it when the data later arrives.

    // ── Operator load emails (Phase 17 / Task 17.2, DEC-324 — migration 0147) ──
    // D-12 tenant defaults, inherited by every customer whose own setting is NULL.
    // Both ship OFF (D-03): nothing is emailed until the hauler turns it on.
    defaultEachHaulEnabled: boolean('default_each_haul_enabled').notNull().default(false),
    // 'OFF' | 'TWO_HOURS' | 'SIX_HOURS' | 'TWELVE_HOURS' | 'DAILY' (CHECK in 0147).
    defaultSummaryCadence: text('default_summary_cadence').notNull().default('OFF'),
    // D-08 — Reply-To and footer contact on load emails. Contact info, not an
    // identifier: never uppercased (DEC-147 exemption, like requester contact fields).
    officeEmail: text('office_email'),
    officePhone: text('office_phone'),
    // D-13 — hauler-wide switch for emailing the ticked requestor on completion.
    requestorLoadEmailsEnabled: boolean('requestor_load_emails_enabled').notNull().default(false),

    // Migration 0250 — the day each ticket is billed on (BILL_DATE_BASES; CHECK in 0250).
    billDateBasis: text('bill_date_basis').$type<BillDateBasis>().notNull().default('FINISHED'),

    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (_t) => [
    // Phase 6 / Plan 06-01 / MEDIUM-4 — mirror the operators_isolation pattern
    // verbatim. The SQL migration 0005 step 9 creates the same-named DB-side
    // policy; this declaration keeps schema-diff tooling aligned.
    // `_t` is unused because the policy predicate only references the
    // `appUserRole` symbol; underscore prefix opts out of the unused-arg lint.
    pgPolicy('hauler_tenant_settings_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerTenantSettings = typeof haulerTenantSettings.$inferSelect;
export type NewHaulerTenantSettings = typeof haulerTenantSettings.$inferInsert;
export const haulerTenantSettingsSelectSchema = createSelectSchema(haulerTenantSettings);
export const haulerTenantSettingsInsertSchema = createInsertSchema(haulerTenantSettings);
